import DOMPurify, { type Config } from 'dompurify';

/**
 * HTML sanitisation for the account editors, replacing `insane@2.6.2` (FTK-7406).
 * `insane` is unmaintained and carries GHSA-w455-mfq9-hf74 / CVE-2020-26303 (ReDoS);
 * 2.6.2 is its final release, so the advisory can only be closed by replacing it.
 *
 * The config below reproduces what insane ACTUALLY did, which is not what its options
 * object claimed. The old `SANITIZE_OPTIONS.allowedAttributes` was `{'*': [...]}`, but
 * insane has no wildcard support — it looked up `allowedAttributes[tagName]`, so that
 * `'*'` entry was never read and what actually applied were insane's own defaults,
 * deep-merged in underneath. The effective allow-list was `a` and `img` only, with every
 * other tag losing all of its attributes. PER_TAG_ALLOWED_ATTRIBUTES below is that
 * effective list, deliberately, not the one the old options object listed: adopting the
 * written one would newly admit class/height/rel/tabindex/width on all 19 tags and
 * href/src on any tag — a silent loosening plus a rendering change on these surfaces.
 *
 * The old `transformText` hook was dropped rather than ported. It called
 * `text.replace('/(...)/g', '')` with a STRING literal rather than a regex, so it matched
 * nothing and was a verified no-op; its stated intent (strip characters forbidden by XML
 * 1.0) never actually ran. The only place those characters mattered for security — using
 * a control character to disguise a URL scheme — is handled by stripUriNoise below.
 */

/**
 * `strong` and `em` were added by FTK-7435. They were absent only because the original insane
 * list predates the editors emitting them, and every other editor in the wild (including
 * Quill 2, whose Bold/Italic blots default to them) does. Their absence was destroying the
 * wrapped text — see the KEEP_CONTENT note below — on 127 stored values.
 *
 * `font` is deliberately NOT here. 10 of the 15 stored occurrences are a bare `<font>` with no
 * attributes at all, and the rest carry hardcoded `color`/`face`/`size`. It is deprecated
 * presentational markup, and KEEP_CONTENT now rescues the text inside it without readmitting
 * the styling — which is the outcome we want on surfaces that do their own theming.
 */
const ALLOWED_TAGS = [
    'a', 'b', 'br', 'div', 'em', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'hr', 'i', 'img', 'li', 'ol', 'p', 'span',
    'strong', 'u', 'ul'
];

/**
 * The attribute allow-list, scoped per tag. DOMPurify's own ALLOWED_ATTR is a flat list
 * with no tag scoping, so the scoping is enforced by the `uponSanitizeAttribute` hook and
 * ALLOWED_ATTR is set to the union purely as a backstop.
 *
 * `style` is deliberately absent (FTK-7435 asked for an explicit decision). Of the 1,227 stored
 * values carrying one, the overwhelming majority are clipboard residue rather than intent:
 * `color: rgb(34, 34, 34); background-color: rgb(255, 255, 255)` is the Gmail paste signature,
 * `font-size: 11pt; ... vertical-align: baseline` the Google Docs one, plus `float: none` and a
 * flat `font-size: 16px` on 398 of them. Admitting those would hardcode a light-theme palette
 * and fixed type sizes into guest-facing surfaces that theme themselves. No text is at stake
 * either way — the elements carrying it are already allowed, so only the attribute is dropped.
 */
const PER_TAG_ALLOWED_ATTRIBUTES = new Map<string, ReadonlySet<string>>([
    ['a', new Set(['href', 'name', 'target', 'title', 'aria-label'])],
    ['img', new Set(['src', 'alt', 'title', 'aria-label'])]
]);

const URI_ATTRIBUTES = new Set(['href', 'src']);

/**
 * Replaces the old `allowedSchemes: ['http', 'https', 'mailto']`. DOMPurify maps schemes to
 * a regexp rather than a list, and its default one also permits ftp/tel/callto/sms/cid/xmpp,
 * so it has to be overridden. The trailing alternations keep relative URLs, `/path` and
 * `#anchor` usable, which insane's own URL check also allowed.
 */
const ALLOWED_URI_REGEXP = /^(?:(?:https?|mailto):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i;

const DATA_URI = /^data:/i;

/** DEL, NBSP, BOM and the replacement character; C0 controls and space are handled by range. */
const URI_NOISE_CODE_POINTS = new Set([0x7f, 0xa0, 0xfeff, 0xfffd]);

/**
 * Strips whitespace and control characters from a URL before it is scheme-checked, so that
 * a NUL, a tab or a replacement character cannot disguise `javascript:` as a relative URL.
 * This only affects the copy being checked; the value that ends up on the attribute is
 * whatever DOMPurify's own whitespace normalisation leaves behind.
 */
function stripUriNoise(value: string): string {
    let stripped = '';
    for (const character of value) {
        const codePoint = character.codePointAt(0) as number;
        if (codePoint > 0x20 && !URI_NOISE_CODE_POINTS.has(codePoint)) {
            stripped += character;
        }
    }
    return stripped;
}

const SANITIZE_CONFIG: Config = {
    // '#text' is redundant while KEEP_CONTENT is true (DOMPurify adds it implicitly then), but
    // is kept explicit so that flipping KEEP_CONTENT back cannot silently strip every text node.
    ALLOWED_TAGS: [...ALLOWED_TAGS, '#text'],
    ALLOWED_ATTR: ['href', 'name', 'target', 'title', 'aria-label', 'src', 'alt'],
    ALLOWED_URI_REGEXP,
    // Both default to true in DOMPurify; insane allowed neither (bar `aria-label` on a/img,
    // which PER_TAG_ALLOWED_ATTRIBUTES grants explicitly).
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
    ALLOW_UNKNOWN_PROTOCOLS: false,
    // FTK-7435. This was false to mirror insane, which discarded a disallowed element's TEXT
    // along with the element. That is data loss, not a formatting choice: `applyChanges` runs on
    // every save, so merely opening and saving a record deleted the copy inside any tag outside
    // the allow-list — 100% of the body where an <em> or <header> wrapped it, and in one stored
    // row the contact email inside a <strong> in the legal terms.
    //
    // True hoists the text out and drops only the wrapper, which is what the allow-list was
    // always assumed to do. It does NOT widen the XSS surface: DOMPurify's FORBID_CONTENTS
    // governs script/style/iframe/svg/math/noscript/title/xmp/textarea independently, so those
    // still lose their contents (see the specs). The one cost is cosmetic — obfuscated markup
    // that used to vanish now leaves inert, entity-escaped text behind.
    KEEP_CONTENT: true
};

function createPurifier(openLinksInNewWindow: boolean) {
    // A scoped instance, not the default export: DOMPurify hooks are global to the instance
    // they are registered on and persist, and jspdf (which the collaborative reports and the
    // spacetime export use) pulls in its own dompurify.
    const purifier = DOMPurify();

    purifier.addHook('uponSanitizeAttribute', (node, data) => {
        if (!PER_TAG_ALLOWED_ATTRIBUTES.get(node.nodeName.toLowerCase())?.has(data.attrName)) {
            data.keepAttr = false;
            return;
        }
        if (!URI_ATTRIBUTES.has(data.attrName)) {
            return;
        }
        const url = stripUriNoise(data.attrValue);
        // DOMPurify allows `data:` on img/audio/video/source/image/track regardless of
        // ALLOWED_URI_REGEXP and offers no config to opt out, so reject it here.
        if (!ALLOWED_URI_REGEXP.test(url) || DATA_URI.test(url)) {
            data.keepAttr = false;
        }
    });

    if (openLinksInNewWindow) {
        // Runs after attribute validation, so these two survive without being in ALLOWED_ATTR
        // — which is what keeps a caller-supplied `rel="opener"` from sticking.
        purifier.addHook('afterSanitizeAttributes', node => {
            if (node.nodeName.toLowerCase() !== 'a') {
                return;
            }
            node.setAttribute('target', '_blank');
            node.setAttribute('rel', 'noopener');
        });
    }

    return purifier;
}

function createSanitizer(openLinksInNewWindow: boolean): (html: string) => string {
    let purifier: ReturnType<typeof DOMPurify> | null = null;
    return (html: string) => {
        if (!html) {
            return '';
        }
        purifier ??= createPurifier(openLinksInNewWindow);
        return purifier.sanitize(html, SANITIZE_CONFIG);
    };
}

/** Sanitises editor HTML, leaving link targets alone. */
export const sanitizeHtml = createSanitizer(false);

/** As {@link sanitizeHtml}, and additionally points every link at a new window. */
export const sanitizeHtmlOpeningLinksInNewWindow = createSanitizer(true);
